import type { TFunction } from 'i18next';

import type { MfaFactor } from '@repo/common';

import { MfaAuthenticatorManageDialog, MfaAuthenticatorRemoveDialog } from './mfa-authenticator-manage';
import { MfaTotpSetupDialogs } from './mfa-authenticator-setup';
import { MfaChooseMethodDialog } from './mfa-choose-method-dialog';
import { MfaDisableConfirmDialog } from './mfa-disable-confirm-dialog';
import { MfaEmailManageDialog, MfaEmailRemoveDialog } from './mfa-email-manage';
import { MfaEmailSetupDialogs } from './mfa-email-setup';
import { MfaExistingFactorDialog } from './mfa-existing-factor-dialog';
import type { MfaPickerMethod } from './mfa-method-picker-utils';
import { type MfaPasskeyCredentialItem, MfaPasskeyManageDialog, MfaPasskeyRemoveDialog } from './mfa-passkey-manage';
import { MfaPasskeySetupDialog } from './mfa-passkey-setup-dialog';
import { MfaReauthDialog } from './mfa-reauth-dialog';
import { MfaRecoveryCodesPanel } from './mfa-recovery-codes-panel';
import { MfaSmsAddMethodDialog } from './mfa-sms-add-method-dialog';
import { MfaSmsManageDialog, MfaSmsRemoveDialog } from './mfa-sms-manage';
import { MfaSmsSetupDialogs } from './mfa-sms-setup';
import type { MfaSettingsDialog } from './use-mfa-settings';

export interface OauthProfile {
  avatarSrc?: string | null;
  continueAsName: string;
  email: string;
  name: string;
}

interface MfaSettingsDialogsProps {
  accountPrimary?: string;
  actionError?: string;
  busy: boolean;
  code: string;
  cooldownSeconds: number;
  dialog: MfaSettingsDialog;
  existingFactor: string;
  existingFactorCode: string;
  enrolled: readonly string[];
  totpEnrolledAt?: string | null;
  totpLastUsedAt?: string | null;
  totpAppName?: string | null;
  emailEnrolledAt?: string | null;
  emailLastUsedAt?: string | null;
  smsEnrolledAt?: string | null;
  smsLastUsedAt?: string | null;
  smsPhoneMasked?: string | null;
  hasPassword: boolean;
  isMobile: boolean;
  isTablet: boolean;
  oauthProfile: OauthProfile;
  passkeyCredentials: readonly MfaPasskeyCredentialItem[];
  onAccountPrimaryChange: (method: MfaFactor) => void;
  onAddEnrolledFactor: (method: MfaFactor) => void;
  onBackToMethods: () => void;
  onCancel: () => void;
  onChooseMethod: (method: MfaFactor) => void;
  onClearActionError: () => void;
  onChangeEmail: () => void;
  onConfirmDisableAll: () => void;
  onConfirmRemoveEmail: (replacement?: MfaPickerMethod) => void;
  onConfirmRemovePasskey: (replacement?: MfaPickerMethod) => void;
  onConfirmRemoveSms: (replacement?: MfaPickerMethod) => void;
  onConfirmRemoveTotp: (replacement?: MfaPickerMethod) => void;
  onConfirmSetup: () => void;
  onConfirmPasskeyRegistration: (responseJson: string) => Promise<boolean | 'backup'>;
  onDisable: () => void;
  onExistingFactorChange: (value: string) => void;
  onExistingFactorCodeChange: (value: string) => void;
  onFinishPrimaryReplace: () => Promise<boolean>;
  onManageEmail: () => void;
  onManagePasskey: () => void;
  onManageSms: () => void;
  onManageTotp: () => void;
  onOauthContinue: () => void;
  onOpenPasskeySetup: () => void;
  onAddAnotherPasskey: () => void;
  onOpenSmsSetup: () => void;
  onChangeSmsPhone: () => void;
  onPasswordChange: (value: string) => void;
  onRemoveEmail: () => void;
  onRemovePasskey: () => void;
  onRemovePasskeyCredential: (credentialId: string) => void;
  onRemovePasskeys: () => void;
  onRemoveSms: () => void;
  onRemoveTotp: () => void;
  onReplaceTotp: () => void;
  onResendEmail: () => Promise<boolean>;
  onSendSmsCode: (phoneE164: string) => Promise<boolean>;
  onSaveTotpAppName: (name: string) => void;
  onSetupCodeChange: (value: string) => void;
  onSubmitExistingFactor: () => void;
  onSubmitReauth: () => void;
  password: string;
  t: TFunction<'common'>;
}

export function MfaSettingsDialogs({
  accountPrimary,
  actionError,
  busy,
  code,
  cooldownSeconds,
  dialog,
  existingFactor,
  existingFactorCode,
  enrolled,
  totpEnrolledAt,
  totpLastUsedAt,
  totpAppName,
  emailEnrolledAt,
  emailLastUsedAt,
  smsEnrolledAt,
  smsLastUsedAt,
  smsPhoneMasked,
  hasPassword,
  isMobile,
  isTablet,
  oauthProfile,
  passkeyCredentials,
  onAccountPrimaryChange,
  onAddEnrolledFactor,
  onBackToMethods,
  onCancel,
  onChooseMethod,
  onClearActionError,
  onChangeEmail,
  onConfirmDisableAll,
  onConfirmRemoveEmail,
  onConfirmRemovePasskey,
  onConfirmRemoveSms,
  onConfirmRemoveTotp,
  onConfirmSetup,
  onConfirmPasskeyRegistration,
  onDisable,
  onExistingFactorChange,
  onExistingFactorCodeChange,
  onFinishPrimaryReplace,
  onManageEmail,
  onManagePasskey,
  onManageSms,
  onManageTotp,
  onOauthContinue,
  onOpenPasskeySetup,
  onAddAnotherPasskey,
  onOpenSmsSetup,
  onChangeSmsPhone,
  onPasswordChange,
  onRemoveEmail,
  onRemovePasskey,
  onRemovePasskeyCredential,
  onRemovePasskeys,
  onRemoveSms,
  onRemoveTotp,
  onReplaceTotp,
  onResendEmail,
  onSendSmsCode,
  onSaveTotpAppName,
  onSetupCodeChange,
  onSubmitExistingFactor,
  onSubmitReauth,
  password,
  t,
}: MfaSettingsDialogsProps) {
  // Each dialog kind is a mutually exclusive view of this settings flow; the switch below is a
  // flat dispatch table (no nested branching) and each arm only forwards already-validated props
  // to an already-extracted dialog component. See the render* helpers below for the per-kind JSX.
  switch (dialog.kind) {
    case 'reauth':
      return renderReauthDialog({
        actionError,
        busy,
        hasPassword,
        isMobile,
        oauthProfile,
        onCancel,
        onOauthContinue,
        onPasswordChange,
        onSubmitReauth,
        password,
        t,
      });

    case 'disable-confirm':
      return renderDisableConfirmDialog({ isMobile, onBackToMethods, onCancel, onConfirmDisableAll, t });

    case 'choose-method':
      return renderChooseMethodDialog({
        accountPrimary,
        enrolled,
        isMobile,
        isTablet,
        oauthProfile,
        passkeyCredentials,
        smsPhoneMasked,
        totpAppName,
        onAccountPrimaryChange,
        onAddEnrolledFactor,
        onCancel,
        onChooseMethod,
        onDisable,
        onManageEmail,
        onManagePasskey,
        onManageSms,
        onManageTotp,
        onOpenSmsSetup,
        t,
      });

    case 'totp-manage':
      return renderTotpManageDialog({
        accountPrimary,
        enrolled,
        totpEnrolledAt,
        isMobile,
        totpLastUsedAt,
        onCancel,
        onRemoveTotp,
        onReplaceTotp,
        onSaveTotpAppName,
        t,
        totpAppName,
      });

    case 'totp-remove':
      return renderTotpRemoveDialog({
        accountPrimary,
        oauthProfile,
        enrolled,
        isMobile,
        onManageTotp,
        onCancel,
        onConfirmRemoveTotp,
        smsPhoneMasked,
        t,
      });

    case 'email-manage':
      return renderEmailManageDialog({
        accountPrimary,
        oauthProfile,
        enrolled,
        emailEnrolledAt,
        isMobile,
        emailLastUsedAt,
        onChangeEmail,
        onCancel,
        onRemoveEmail,
        t,
      });

    case 'email-remove':
      return renderEmailRemoveDialog({
        accountPrimary,
        enrolled,
        isMobile,
        onManageEmail,
        onCancel,
        onConfirmRemoveEmail,
        smsPhoneMasked,
        t,
      });

    case 'sms-manage':
      return renderSmsManageDialog({
        accountPrimary,
        enrolled,
        smsEnrolledAt,
        isMobile,
        smsLastUsedAt,
        onChangeSmsPhone,
        onCancel,
        onRemoveSms,
        smsPhoneMasked,
        t,
      });

    case 'sms-remove':
      return renderSmsRemoveDialog({
        accountPrimary,
        oauthProfile,
        enrolled,
        isMobile,
        onManageSms,
        onCancel,
        onConfirmRemoveSms,
        smsPhoneMasked,
        t,
      });

    case 'sms-add-method':
      return renderSmsAddMethodDialog({
        enrolled,
        isMobile,
        onAddEnrolledFactor,
        onCancel,
        onChooseMethod,
        onOpenPasskeySetup,
        onRemoveSms,
        t,
      });

    case 'existing-factor':
      return renderExistingFactorDialog({
        actionError,
        busy,
        cooldownSeconds,
        enrolled,
        existingFactor,
        existingFactorCode,
        isMobile,
        onCancel,
        onExistingFactorChange,
        onExistingFactorCodeChange,
        onResendEmail,
        onSubmitExistingFactor,
        t,
      });

    case 'totp-setup':
      return renderTotpSetupDialog(dialog, {
        actionError,
        busy,
        code,
        isMobile,
        onBackToMethods,
        onCancel,
        onChooseMethod,
        onClearActionError,
        onConfirmSetup,
        onSetupCodeChange,
        t,
      });

    case 'sms-setup':
      return renderSmsSetupDialog(dialog, {
        actionError,
        busy,
        code,
        cooldownSeconds,
        isMobile,
        onBackToMethods,
        onCancel,
        onClearActionError,
        onConfirmSetup,
        onManageSms,
        onResendEmail,
        onSendSmsCode,
        onSetupCodeChange,
        t,
      });

    case 'passkey-setup':
      return renderPasskeySetupDialog(dialog, {
        isMobile,
        onBackToMethods,
        onCancel,
        onConfirmPasskeyRegistration,
        onFinishPrimaryReplace,
        onManagePasskey,
        t,
      });

    case 'passkey-manage':
      return renderPasskeyManageDialog({
        accountPrimary,
        passkeyCredentials,
        enrolled,
        isMobile,
        onAddAnotherPasskey,
        onCancel,
        onRemovePasskeyCredential,
        onRemovePasskey,
        onRemovePasskeys,
        t,
      });

    case 'passkey-remove':
      return renderPasskeyRemoveDialog({
        accountPrimary,
        oauthProfile,
        enrolled,
        isMobile,
        onManagePasskey,
        onCancel,
        onConfirmRemovePasskey,
        passkeyCredentials,
        smsPhoneMasked,
        t,
      });

    case 'email-setup':
      return renderEmailSetupDialog({
        actionError,
        busy,
        code,
        cooldownSeconds,
        oauthProfile,
        isMobile,
        onBackToMethods,
        onCancel,
        onClearActionError,
        onConfirmSetup,
        onResendEmail,
        onSetupCodeChange,
        t,
      });

    case 'backup-codes':
      return renderBackupCodesDialog(dialog, { isMobile, onCancel, t });

    default:
      return null;
  }
}

function renderReauthDialog({
  actionError,
  busy,
  hasPassword,
  isMobile,
  oauthProfile,
  onCancel,
  onOauthContinue,
  onPasswordChange,
  onSubmitReauth,
  password,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'actionError'
  | 'busy'
  | 'hasPassword'
  | 'isMobile'
  | 'oauthProfile'
  | 'onCancel'
  | 'onOauthContinue'
  | 'onPasswordChange'
  | 'onSubmitReauth'
  | 'password'
  | 't'
>) {
  return (
    <MfaReauthDialog
      actionError={actionError}
      busy={busy}
      hasPassword={hasPassword}
      isMobile={isMobile}
      oauthProfile={oauthProfile}
      onCancel={onCancel}
      onOauthContinue={onOauthContinue}
      onPasswordChange={onPasswordChange}
      onSubmitReauth={onSubmitReauth}
      password={password}
      t={t}
    />
  );
}

function renderDisableConfirmDialog({
  isMobile,
  onBackToMethods,
  onCancel,
  onConfirmDisableAll,
  t,
}: Pick<MfaSettingsDialogsProps, 'isMobile' | 'onBackToMethods' | 'onCancel' | 'onConfirmDisableAll' | 't'>) {
  return (
    <MfaDisableConfirmDialog
      isMobile={isMobile}
      onBackToMethods={onBackToMethods}
      onCancel={onCancel}
      onConfirmDisableAll={onConfirmDisableAll}
      t={t}
    />
  );
}

function renderChooseMethodDialog({
  accountPrimary,
  enrolled,
  isMobile,
  isTablet,
  oauthProfile,
  passkeyCredentials,
  smsPhoneMasked,
  totpAppName,
  onAccountPrimaryChange,
  onAddEnrolledFactor,
  onCancel,
  onChooseMethod,
  onDisable,
  onManageEmail,
  onManagePasskey,
  onManageSms,
  onManageTotp,
  onOpenSmsSetup,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'enrolled'
  | 'isMobile'
  | 'isTablet'
  | 'oauthProfile'
  | 'passkeyCredentials'
  | 'smsPhoneMasked'
  | 'totpAppName'
  | 'onAccountPrimaryChange'
  | 'onAddEnrolledFactor'
  | 'onCancel'
  | 'onChooseMethod'
  | 'onDisable'
  | 'onManageEmail'
  | 'onManagePasskey'
  | 'onManageSms'
  | 'onManageTotp'
  | 'onOpenSmsSetup'
  | 't'
>) {
  return (
    <MfaChooseMethodDialog
      accountPrimary={accountPrimary}
      enrolled={enrolled}
      isMobile={isMobile}
      isTablet={isTablet}
      oauthProfile={oauthProfile}
      passkeyCredentials={passkeyCredentials}
      smsPhoneMasked={smsPhoneMasked}
      totpAppName={totpAppName}
      onAccountPrimaryChange={onAccountPrimaryChange}
      onAddEnrolledFactor={onAddEnrolledFactor}
      onCancel={onCancel}
      onChooseMethod={onChooseMethod}
      onDisable={onDisable}
      onManageEmail={onManageEmail}
      onManagePasskey={onManagePasskey}
      onManageSms={onManageSms}
      onManageTotp={onManageTotp}
      onOpenSmsSetup={onOpenSmsSetup}
      t={t}
    />
  );
}

function renderTotpManageDialog({
  accountPrimary,
  enrolled,
  totpEnrolledAt,
  isMobile,
  totpLastUsedAt,
  onCancel,
  onRemoveTotp,
  onReplaceTotp,
  onSaveTotpAppName,
  t,
  totpAppName,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'enrolled'
  | 'totpEnrolledAt'
  | 'isMobile'
  | 'totpLastUsedAt'
  | 'onCancel'
  | 'onRemoveTotp'
  | 'onReplaceTotp'
  | 'onSaveTotpAppName'
  | 't'
  | 'totpAppName'
>) {
  return (
    <MfaAuthenticatorManageDialog
      accountPrimary={accountPrimary}
      enrolled={enrolled}
      enrolledAt={totpEnrolledAt}
      isMobile={isMobile}
      lastUsedAt={totpLastUsedAt}
      onClose={onCancel}
      onRemove={onRemoveTotp}
      onReplace={onReplaceTotp}
      onSaveAppName={onSaveTotpAppName}
      t={t}
      totpAppName={totpAppName}
    />
  );
}

function renderTotpRemoveDialog({
  accountPrimary,
  oauthProfile,
  enrolled,
  isMobile,
  onManageTotp,
  onCancel,
  onConfirmRemoveTotp,
  smsPhoneMasked,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'oauthProfile'
  | 'enrolled'
  | 'isMobile'
  | 'onManageTotp'
  | 'onCancel'
  | 'onConfirmRemoveTotp'
  | 'smsPhoneMasked'
  | 't'
>) {
  return (
    <MfaAuthenticatorRemoveDialog
      accountPrimary={accountPrimary}
      email={oauthProfile.email}
      enrolled={enrolled}
      isMobile={isMobile}
      onBack={onManageTotp}
      onClose={onCancel}
      onConfirm={onConfirmRemoveTotp}
      smsPhoneMasked={smsPhoneMasked}
      t={t}
    />
  );
}

function renderEmailManageDialog({
  accountPrimary,
  oauthProfile,
  enrolled,
  emailEnrolledAt,
  isMobile,
  emailLastUsedAt,
  onChangeEmail,
  onCancel,
  onRemoveEmail,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'oauthProfile'
  | 'enrolled'
  | 'emailEnrolledAt'
  | 'isMobile'
  | 'emailLastUsedAt'
  | 'onChangeEmail'
  | 'onCancel'
  | 'onRemoveEmail'
  | 't'
>) {
  return (
    <MfaEmailManageDialog
      accountPrimary={accountPrimary}
      email={oauthProfile.email}
      enrolled={enrolled}
      enrolledAt={emailEnrolledAt}
      isMobile={isMobile}
      lastUsedAt={emailLastUsedAt}
      onChangeEmail={onChangeEmail}
      onClose={onCancel}
      onRemove={onRemoveEmail}
      t={t}
    />
  );
}

function renderEmailRemoveDialog({
  accountPrimary,
  enrolled,
  isMobile,
  onManageEmail,
  onCancel,
  onConfirmRemoveEmail,
  smsPhoneMasked,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'enrolled'
  | 'isMobile'
  | 'onManageEmail'
  | 'onCancel'
  | 'onConfirmRemoveEmail'
  | 'smsPhoneMasked'
  | 't'
>) {
  return (
    <MfaEmailRemoveDialog
      accountPrimary={accountPrimary}
      enrolled={enrolled}
      isMobile={isMobile}
      onBack={onManageEmail}
      onClose={onCancel}
      onConfirm={onConfirmRemoveEmail}
      smsPhoneMasked={smsPhoneMasked}
      t={t}
    />
  );
}

function renderSmsManageDialog({
  accountPrimary,
  enrolled,
  smsEnrolledAt,
  isMobile,
  smsLastUsedAt,
  onChangeSmsPhone,
  onCancel,
  onRemoveSms,
  smsPhoneMasked,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'enrolled'
  | 'smsEnrolledAt'
  | 'isMobile'
  | 'smsLastUsedAt'
  | 'onChangeSmsPhone'
  | 'onCancel'
  | 'onRemoveSms'
  | 'smsPhoneMasked'
  | 't'
>) {
  return (
    <MfaSmsManageDialog
      accountPrimary={accountPrimary}
      enrolled={enrolled}
      enrolledAt={smsEnrolledAt}
      isMobile={isMobile}
      lastUsedAt={smsLastUsedAt}
      onChangePhone={onChangeSmsPhone}
      onClose={onCancel}
      onRemove={onRemoveSms}
      smsPhoneMasked={smsPhoneMasked}
      t={t}
    />
  );
}

function renderSmsRemoveDialog({
  accountPrimary,
  oauthProfile,
  enrolled,
  isMobile,
  onManageSms,
  onCancel,
  onConfirmRemoveSms,
  smsPhoneMasked,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'oauthProfile'
  | 'enrolled'
  | 'isMobile'
  | 'onManageSms'
  | 'onCancel'
  | 'onConfirmRemoveSms'
  | 'smsPhoneMasked'
  | 't'
>) {
  return (
    <MfaSmsRemoveDialog
      accountPrimary={accountPrimary}
      email={oauthProfile.email}
      enrolled={enrolled}
      isMobile={isMobile}
      onBack={onManageSms}
      onClose={onCancel}
      onConfirm={onConfirmRemoveSms}
      smsPhoneMasked={smsPhoneMasked}
      t={t}
    />
  );
}

function renderSmsAddMethodDialog({
  enrolled,
  isMobile,
  onAddEnrolledFactor,
  onCancel,
  onChooseMethod,
  onOpenPasskeySetup,
  onRemoveSms,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'enrolled'
  | 'isMobile'
  | 'onAddEnrolledFactor'
  | 'onCancel'
  | 'onChooseMethod'
  | 'onOpenPasskeySetup'
  | 'onRemoveSms'
  | 't'
>) {
  return (
    <MfaSmsAddMethodDialog
      enrolled={enrolled}
      isMobile={isMobile}
      onAddEnrolledFactor={onAddEnrolledFactor}
      onCancel={onCancel}
      onChooseMethod={onChooseMethod}
      onOpenPasskeySetup={onOpenPasskeySetup}
      onRemoveSms={onRemoveSms}
      t={t}
    />
  );
}

function renderExistingFactorDialog({
  actionError,
  busy,
  cooldownSeconds,
  enrolled,
  existingFactor,
  existingFactorCode,
  isMobile,
  onCancel,
  onExistingFactorChange,
  onExistingFactorCodeChange,
  onResendEmail,
  onSubmitExistingFactor,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'actionError'
  | 'busy'
  | 'cooldownSeconds'
  | 'enrolled'
  | 'existingFactor'
  | 'existingFactorCode'
  | 'isMobile'
  | 'onCancel'
  | 'onExistingFactorChange'
  | 'onExistingFactorCodeChange'
  | 'onResendEmail'
  | 'onSubmitExistingFactor'
  | 't'
>) {
  return (
    <MfaExistingFactorDialog
      actionError={actionError}
      busy={busy}
      cooldownSeconds={cooldownSeconds}
      enrolled={enrolled}
      existingFactor={existingFactor}
      existingFactorCode={existingFactorCode}
      isMobile={isMobile}
      onCancel={onCancel}
      onExistingFactorChange={onExistingFactorChange}
      onExistingFactorCodeChange={onExistingFactorCodeChange}
      onResendEmail={onResendEmail}
      onSubmitExistingFactor={onSubmitExistingFactor}
      t={t}
    />
  );
}

function renderTotpSetupDialog(
  dialog: Extract<MfaSettingsDialog, { kind: 'totp-setup' }>,
  {
    actionError,
    busy,
    code,
    isMobile,
    onBackToMethods,
    onCancel,
    onChooseMethod,
    onClearActionError,
    onConfirmSetup,
    onSetupCodeChange,
    t,
  }: Pick<
    MfaSettingsDialogsProps,
    | 'actionError'
    | 'busy'
    | 'code'
    | 'isMobile'
    | 'onBackToMethods'
    | 'onCancel'
    | 'onChooseMethod'
    | 'onClearActionError'
    | 'onConfirmSetup'
    | 'onSetupCodeChange'
    | 't'
  >,
) {
  return (
    <MfaTotpSetupDialogs
      // A fresh secret means a new enrollment attempt: remount instead of
      // reaching into this instance to reset its intro/manual-key state.
      key={dialog.otpauthUri}
      actionError={actionError}
      busy={busy}
      code={code}
      isMobile={isMobile}
      onBackToMethods={onBackToMethods}
      onCancel={onCancel}
      onChooseMethod={onChooseMethod}
      onClearActionError={onClearActionError}
      onConfirmSetup={onConfirmSetup}
      onSetupCodeChange={onSetupCodeChange}
      otpauthUri={dialog.otpauthUri}
      secret={dialog.secret}
      t={t}
    />
  );
}

function renderSmsSetupDialog(
  dialog: Extract<MfaSettingsDialog, { kind: 'sms-setup' }>,
  {
    actionError,
    busy,
    code,
    cooldownSeconds,
    isMobile,
    onBackToMethods,
    onCancel,
    onClearActionError,
    onConfirmSetup,
    onManageSms,
    onResendEmail,
    onSendSmsCode,
    onSetupCodeChange,
    t,
  }: Pick<
    MfaSettingsDialogsProps,
    | 'actionError'
    | 'busy'
    | 'code'
    | 'cooldownSeconds'
    | 'isMobile'
    | 'onBackToMethods'
    | 'onCancel'
    | 'onClearActionError'
    | 'onConfirmSetup'
    | 'onManageSms'
    | 'onResendEmail'
    | 'onSendSmsCode'
    | 'onSetupCodeChange'
    | 't'
  >,
) {
  return (
    <MfaSmsSetupDialogs
      actionError={actionError}
      busy={busy}
      changePhone={Boolean(dialog.changePhone)}
      code={code}
      cooldownSeconds={cooldownSeconds}
      isMobile={isMobile}
      onBackToMethods={onBackToMethods}
      onCancel={onCancel}
      onClearActionError={onClearActionError}
      onConfirmSetup={onConfirmSetup}
      onManageSms={onManageSms}
      onResendSms={onResendEmail}
      onSendSmsCode={onSendSmsCode}
      onSetupCodeChange={onSetupCodeChange}
      phoneNumberSent={dialog.phoneNumber}
      t={t}
    />
  );
}

function renderPasskeySetupDialog(
  dialog: Extract<MfaSettingsDialog, { kind: 'passkey-setup' }>,
  {
    isMobile,
    onBackToMethods,
    onCancel,
    onConfirmPasskeyRegistration,
    onFinishPrimaryReplace,
    onManagePasskey,
    t,
  }: Pick<
    MfaSettingsDialogsProps,
    | 'isMobile'
    | 'onBackToMethods'
    | 'onCancel'
    | 'onConfirmPasskeyRegistration'
    | 'onFinishPrimaryReplace'
    | 'onManagePasskey'
    | 't'
  >,
) {
  return (
    <MfaPasskeySetupDialog
      addAnother={Boolean(dialog.addAnother)}
      isMobile={isMobile}
      onBackToMethods={onBackToMethods}
      onCancel={onCancel}
      onConfirmPasskeyRegistration={onConfirmPasskeyRegistration}
      onFinishPrimaryReplace={onFinishPrimaryReplace}
      onManagePasskey={onManagePasskey}
      optionsJson={dialog.optionsJson}
      t={t}
    />
  );
}

function renderPasskeyManageDialog({
  accountPrimary,
  passkeyCredentials,
  enrolled,
  isMobile,
  onAddAnotherPasskey,
  onCancel,
  onRemovePasskeyCredential,
  onRemovePasskey,
  onRemovePasskeys,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'passkeyCredentials'
  | 'enrolled'
  | 'isMobile'
  | 'onAddAnotherPasskey'
  | 'onCancel'
  | 'onRemovePasskeyCredential'
  | 'onRemovePasskey'
  | 'onRemovePasskeys'
  | 't'
>) {
  return (
    <MfaPasskeyManageDialog
      accountPrimary={accountPrimary}
      credentials={passkeyCredentials}
      enrolled={enrolled}
      isMobile={isMobile}
      onAddAnother={onAddAnotherPasskey}
      onClose={onCancel}
      onRemoveCredential={onRemovePasskeyCredential}
      onRemoveLast={onRemovePasskey}
      onRemoveAll={onRemovePasskeys}
      t={t}
    />
  );
}

function renderPasskeyRemoveDialog({
  accountPrimary,
  oauthProfile,
  enrolled,
  isMobile,
  onManagePasskey,
  onCancel,
  onConfirmRemovePasskey,
  passkeyCredentials,
  smsPhoneMasked,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'accountPrimary'
  | 'oauthProfile'
  | 'enrolled'
  | 'isMobile'
  | 'onManagePasskey'
  | 'onCancel'
  | 'onConfirmRemovePasskey'
  | 'passkeyCredentials'
  | 'smsPhoneMasked'
  | 't'
>) {
  return (
    <MfaPasskeyRemoveDialog
      accountPrimary={accountPrimary}
      email={oauthProfile.email}
      enrolled={enrolled}
      isMobile={isMobile}
      onBack={onManagePasskey}
      onClose={onCancel}
      onConfirm={onConfirmRemovePasskey}
      passkeyCount={passkeyCredentials.length}
      smsPhoneMasked={smsPhoneMasked}
      t={t}
    />
  );
}

function renderEmailSetupDialog({
  actionError,
  busy,
  code,
  cooldownSeconds,
  oauthProfile,
  isMobile,
  onBackToMethods,
  onCancel,
  onClearActionError,
  onConfirmSetup,
  onResendEmail,
  onSetupCodeChange,
  t,
}: Pick<
  MfaSettingsDialogsProps,
  | 'actionError'
  | 'busy'
  | 'code'
  | 'cooldownSeconds'
  | 'oauthProfile'
  | 'isMobile'
  | 'onBackToMethods'
  | 'onCancel'
  | 'onClearActionError'
  | 'onConfirmSetup'
  | 'onResendEmail'
  | 'onSetupCodeChange'
  | 't'
>) {
  return (
    <MfaEmailSetupDialogs
      actionError={actionError}
      busy={busy}
      code={code}
      cooldownSeconds={cooldownSeconds}
      email={oauthProfile.email}
      isMobile={isMobile}
      onBackToMethods={onBackToMethods}
      onCancel={onCancel}
      onClearActionError={onClearActionError}
      onConfirmSetup={onConfirmSetup}
      onResendEmail={onResendEmail}
      onSetupCodeChange={onSetupCodeChange}
      t={t}
    />
  );
}

function renderBackupCodesDialog(
  dialog: Extract<MfaSettingsDialog, { kind: 'backup-codes' }>,
  { isMobile, onCancel, t }: Pick<MfaSettingsDialogsProps, 'isMobile' | 'onCancel' | 't'>,
) {
  return (
    <MfaRecoveryCodesPanel codes={dialog.codes} isMobile={isMobile} onClose={onCancel} t={t} variant={dialog.variant} />
  );
}
